import { ApostleSkillExp } from '@apeiron/apostle'
import { BOUNTIES_PAGE_SIZE } from '@apeiron/bounty'
import { useCurrencyContext } from '@apeiron/currency'
import {
  ApeironPagingDataGrid,
  ApeironPagingDataGridRef,
  CARD_USD_OPTIONS,
  MayNull,
  NextImage,
  PagingDatasourceResult
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ApostleSkillCard from '@src/components/apostles/card/ApostleSkillCard'
import PriceText from '@src/components/share/generic/PriceText'
import { ApostleSkillsProvider } from '@src/contexts/apostle/ApostleSkillsContext'
import useApostleHelper from '@src/hooks/apeiron/apostle/useApostleHelper'
import { FetchBountiesResponse } from '@src/hooks/graphql/useGetBounties'
import { BountyNode } from '@src/types/bounty'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { format } from 'date-fns'
import { ReactNode, RefObject, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const StyledPagingDataGrid = styled(ApeironPagingDataGrid)`
  margin: auto;
  max-width: 1200px;
  padding: 1%;
  width: 95%;
  .Custom-Grid {
    .ag-row {
      border-bottom: 1px solid #232326;
    }
    .ag-header-row {
      background-color: #151615;
    }
  }
`

const ApostleIcon = styled(NextImage)`
  height: 24px;
  width: 24px;
  aspect-ratio: 1;
`

const Text = styled(Box)`
  display: flex;
  gap: 8px;
`

const StyledApostleSkillCard = styled(ApostleSkillCard)`
  height: 66px;
`

const SkillList = styled(Box)`
  display: flex;
  height: 80px;
  align-items: center;
  justify-content: center;
  width: 150px;
`

const GrayText = styled(Typography)`
  color: #9b9b9b;
  font-size: 14px;
`

const RewardContainer = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  width: 120px;
`

const StyledPriceText = styled(PriceText)`
  .Custom-Icon {
    width: 16px;
    height: 16px;
  }
  .Custom-Text {
    color: white;
  }
`

const USDPriceText = styled(PriceText)`
  .Custom-Text {
    color: #ffffff99;
    font-size: 12px;
    font-weight: 300;
    line-height: 12px;
  }
`

const CenterBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ApostleBountiesDataGrid = ({
  showActionButtons = true,
  showClosedAt = false,
  renderActions,
  fetchBounties,
  onFetchSuccess,
  gridRef,
  defaultPage = 1
}: Props) => {
  const { t } = useTranslation()

  const { getApeironPrice, getCurrency, getUSDPrice } = useCurrencyContext()

  const { getApostleClassByType } = useApostleHelper()

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        cellClass: 'custom-ag-cell-center',
        field: 'class',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.class'),
        width: 160,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const apostleClass = getApostleClassByType(
            params.data?.bountyRequired.apostleType
          )

          if (!apostleClass) {
            return null
          }

          return (
            <Text>
              <ApostleIcon src={apostleClass.icon} />
              {apostleClass.name}
            </Text>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'minIV',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.min_iv'),
        width: 120,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const mivIV = params.data?.bountyRequired.minIV

          if (!mivIV) {
            return '*'
          }

          return <GrayText>{mivIV}</GrayText>
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'firstSkill',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.first_skill'),
        width: 120,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const firstSkill: ApostleSkillExp = {
            id: params.data?.bountyRequired.mainSkillID || 0,
            key: params.data?.bountyRequired.mainSkillFullID || '',
            level: params.data?.bountyRequired.minMainSkillLevel || -1
          }

          if (!firstSkill.key) {
            return '*'
          }

          return (
            <SkillList>
              <StyledApostleSkillCard
                key={firstSkill.key}
                skillExp={firstSkill}
              />
            </SkillList>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'otherSkills',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.other_skills'),
        width: 150,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const {
            otherSkillFullID1,
            otherSkillFullID2,
            minOtherSkill1Level,
            minOtherSkill2Level
          } = params.data?.bountyRequired || {}

          const skills = [
            {
              key: otherSkillFullID1,
              level: minOtherSkill1Level || -1
            },
            {
              key: otherSkillFullID2,
              level: minOtherSkill2Level || -1
            }
          ]

          if (!otherSkillFullID1 && !otherSkillFullID2) {
            return '*'
          }

          return (
            <SkillList>
              {skills.map(item => {
                if (!item.key) {
                  return null
                }

                return (
                  <StyledApostleSkillCard
                    key={item.key}
                    skillExp={item as ApostleSkillExp}
                  />
                )
              })}
            </SkillList>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-right',
        field: 'reward',
        headerClass: 'custom-ag-header-right',
        headerName: t('bounty.apostle.grid_header.reward'),
        width: 140,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const reward = params.data?.unitPrice

          const currencyType = params.data?.currencyType

          const currency = getCurrency(currencyType)

          const usd = getUSDPrice(currencyType, reward, true, CARD_USD_OPTIONS)

          return (
            <>
              <RewardContainer>
                <StyledPriceText
                  icon={currency?.image}
                  displayPrice={getApeironPrice(currencyType, reward)}
                />
                <USDPriceText prefix='~$' displayPrice={usd} />
              </RewardContainer>
            </>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'quantity',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.quantity'),
        width: 200,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          return (
            <GrayText>
              {params.data?.remainingBountyNumber}/
              {params.data?.initBountyNumber}
            </GrayText>
          )
        }
      }
    ]

    if (showClosedAt) {
      cols.push({
        cellClass: 'custom-ag-cell-center',
        field: 'closedAt',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.closed_at'),
        width: 180,
        flex: 1,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          const bounty = params.data

          if (!bounty) {
            return '-'
          }

          const isCancelled = bounty.isCancelled

          const date = isCancelled ? bounty.cancelledAt : bounty.fulfilledAt

          if (!date) {
            return '-'
          }

          return (
            <CenterBox>
              {format(new Date(date), 'dd/MM/yyyy')}
              {isCancelled && <GrayText>{t('bounty.cancelled')}</GrayText>}
            </CenterBox>
          )
        }
      })
    }

    if (showActionButtons) {
      cols.push({
        cellClass: 'custom-ag-cell-center',
        field: 'action',
        headerClass: 'custom-ag-header-center',
        headerName: t('bounty.apostle.grid_header.action'),
        width: 180,
        minWidth: 180,
        flex: 1,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<BountyNode>) => {
          return renderActions ? renderActions(params.data) : null
        }
      })
    }

    return cols
  }, [
    t,
    showClosedAt,
    showActionButtons,
    getApostleClassByType,
    getCurrency,
    getUSDPrice,
    getApeironPrice,
    renderActions
  ])

  const handleOnFetch = useCallback(
    async (page: number, pageSize: number): Promise<PagingDatasourceResult> => {
      const result = await fetchBounties(page, pageSize)

      onFetchSuccess?.(result, { page, pageSize })

      return {
        data: result?.items || [],
        total: result?.pageInfo.totalCount || 0
      }
    },
    [fetchBounties, onFetchSuccess]
  )

  return (
    <ApostleSkillsProvider>
      <StyledPagingDataGrid
        domLayout={'autoHeight'}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowHeight={80}
        fetch={handleOnFetch}
        pageSize={BOUNTIES_PAGE_SIZE}
        defaultPage={defaultPage}
      />
    </ApostleSkillsProvider>
  )
}

type Props = {
  showActionButtons?: boolean
  showClosedAt?: boolean
  fetchBounties: (
    page: number,
    pageSize: number
  ) => Promise<MayNull<FetchBountiesResponse>>
  renderActions?: (bounty: BountyNode | undefined) => ReactNode
  onFetchSuccess?: (
    result: MayNull<FetchBountiesResponse>,
    params: { page: number; pageSize: number }
  ) => void
  gridRef: RefObject<ApeironPagingDataGridRef>
  defaultPage?: number
}

export default ApostleBountiesDataGrid
